import React, { useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

/**
 * Props for SetPasswordForm component
 */
type Args = {
    /** Password reset token received from email */
    passwordResetToken: string;
    /** Callback function called when form is submitted with valid password */
    submitted: (password: string) => void;
};

/**
 * Form component for setting new password. Validates password length and match with confirm password.
 * @param {Args} props - Props for SetPasswordForm component
 */
const SetPasswordForm = ({ passwordResetToken, submitted }: Args) => {
    // Password states
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    // Validation states
    const [passwordLengthValid, setPasswordLengthValid] = useState(false);
    const [passwordsMatch, setPasswordsMatch] = useState(false);

    const { t } = useTranslation();

    // Validates password length and match with confirm password
    const validatePassword = () => {
        const lengthValid = password.length >= 15;
        const match = password === confirmPassword;

        setPasswordLengthValid(lengthValid);
        setPasswordsMatch(match);

        return lengthValid && match;
    };

    /**
     * Handles form submission for setting new password
     * @param e - Click event
     */
    const onSubmit: React.MouseEventHandler<HTMLButtonElement> = async (e) => {
        e.preventDefault();
        if (validatePassword()) {
            submitted(password);
        } else {
            toast.error(t('password.invalid'));
        }
    };

    if (!passwordResetToken) {
        return <div>{t('password.invalidToken')}</div>;
    }

    return (
        <div className="ml-3">
            <h5 className="subtitle text-left mb-2">{t('password.setpassword')}</h5>
            <form>
                <div className="form-group">
                    <label htmlFor="password">{t('common.password')}</label>
                    <div className="input-group">
                        <input
                            type={showPassword ? "text" : "password"}
                            maxLength={64}
                            className="form-control"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onBlur={validatePassword}
                            onKeyUp={() => {
                                if (password.length > 0) {
                                    validatePassword();
                                }
                            }}
                        />
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => setShowPassword(!showPassword)}
                        >
                            {showPassword ? t('common.hide') : (t('common.show'))}
                        </button>
                    </div>
                </div>
                <div className="form-group">
                    <label htmlFor="confirmPassword">{t('user.confirmPassword')}</label>
                    <div className="input-group">
                        <input
                            type={showPassword ? "text" : "password"}
                            maxLength={64}
                            className="form-control"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            onBlur={validatePassword}
                            onKeyUp={() => {
                                if (password.length > 0) {
                                    validatePassword();
                                }
                            }
                            }
                        />
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => setShowPassword(!showPassword)}
                        >
                            {showPassword ? t('common.hide') : (t('common.show'))}
                        </button>
                    </div>
                </div>
                <div className="form-group">
                    <ul className="text-danger list-unstyled">
                        <li>
                            <span style={{ color: passwordLengthValid ? "green" : "red" }}
                                role="status"
                                aria-label={passwordLengthValid ? t('user.passwordRuleMatched') : t('user.passwordRuleNotMatched')}>
                                {passwordLengthValid ? '✓' : '✗'}
                                <span className="ms-2">{t('user.passwordLeastFifteenCharacters')}</span>
                            </span>
                        </li>
                        <li>
                            <span style={{ color: passwordsMatch ? "green" : "red" }}
                                role="status"
                                aria-label={passwordsMatch ? t('user.passwordRuleMatched') : t('user.passwordRuleNotMatched')}>
                                {passwordsMatch ? '✓' : '✗'}
                                <span className="ms-2">{t('user.passwordDoesNotMatch')}</span>
                            </span>
                        </li>
                    </ul>
                </div>
                <button
                    className="btn btn-primary mt-3 me-2 mb-2"
                    onClick={onSubmit}>
                    {t('buttons.save')}
                </button>
            </form>
        </div>
    );
};

export default SetPasswordForm;